import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import WithdrawModal from '../../components/WithdrawModal'; // পাথ ঠিক আছে কিনা চেক করুন

const AffiliateDashboard = () => {
    const [data, setData] = useState(null);
    const [isWithdrawOpen, setIsWithdrawOpen] = useState(false); // মডাল স্টেট
    const navigate = useNavigate();
    const token = localStorage.getItem('affiliateToken');

    // ডাটা লোড ফাংশন (যাতে রিকোয়েস্টের পর আবার কল করা যায়)
    const loadDashboardData = useCallback(() => {
        if (!token) { navigate('/affiliate/login'); return; }

        fetch(`${import.meta.env.VITE_API_URL}/affiliate/dashboard`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(res => res.json())
        .then(data => setData(data))
        .catch(() => navigate('/affiliate/login'));
    }, [token, navigate]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    if (!data) return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">Loading Dashboard...</div>;

    const { profile, total_referrals, recent_commissions } = data;
    const referralLink = `${window.location.origin}/register?ref=${profile.ref_code}`;

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-6 font-sans">
            <div className="max-w-6xl mx-auto">
                
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Partner Dashboard</h1>
                        <p className="text-gray-400">Welcome back, {profile.full_name}</p>
                    </div>
                    <button 
                        onClick={() => { localStorage.removeItem('affiliateToken'); navigate('/affiliate/login'); }} 
                        className="bg-red-600/10 text-red-500 border border-red-600/50 px-4 py-2 rounded hover:bg-red-600 hover:text-white transition"
                    >
                        Logout
                    </button>
                </div>

                {/* Referral Link Card */}
                <div className="bg-gradient-to-r from-indigo-900 to-blue-900 p-6 rounded-xl border border-indigo-700 mb-8 flex flex-col md:flex-row justify-between items-center gap-4 shadow-lg">
                    <div>
                        <h3 className="text-lg font-bold text-indigo-100">Your Unique Referral Link</h3>
                        <p className="text-sm text-indigo-300">Share this link with doctors to earn 20% commission per sale.</p>
                    </div>
                    <div className="flex bg-black/30 p-2 rounded-lg border border-indigo-500/30 items-center">
                        <code className="text-blue-300 px-3 select-all font-mono">{referralLink}</code>
                        <button 
                            onClick={() => {
                                navigator.clipboard.writeText(referralLink);
                                alert("Link Copied!");
                            }} 
                            className="ml-2 text-xs bg-indigo-600 px-3 py-1.5 rounded hover:bg-indigo-500 text-white font-bold transition"
                        >
                            Copy
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Wallet Card */}
                    <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 shadow-sm relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                            <span className="text-6xl">💰</span>
                        </div>
                        <h3 className="text-gray-400 text-sm uppercase font-bold tracking-wider">Wallet Balance</h3>
                        <p className="text-4xl font-extrabold text-green-400 mt-2">৳ {profile.wallet_balance}</p>
                        
                        <button 
                            onClick={() => setIsWithdrawOpen(true)}
                            className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-bold transition disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2" 
                            disabled={Number(profile.wallet_balance) < 500}
                        >
                            {Number(profile.wallet_balance) < 500 ? 'Min Withdraw ৳500' : 'Request Withdraw'}
                        </button>
                    </div>

                    {/* Total Earned */}
                    <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 shadow-sm">
                        <h3 className="text-gray-400 text-sm uppercase font-bold tracking-wider">Lifetime Earnings</h3>
                        <p className="text-4xl font-extrabold text-white mt-2">৳ {profile.total_earned}</p>
                        <p className="text-gray-500 text-sm mt-4">Total processed income</p>
                    </div>

                    {/* Total Referrals */}
                    <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 shadow-sm">
                        <h3 className="text-gray-400 text-sm uppercase font-bold tracking-wider">Doctors Referred</h3>
                        <p className="text-4xl font-extrabold text-blue-400 mt-2">{total_referrals}</p>
                        <p className="text-gray-500 text-sm mt-4">Successful registrations</p>
                    </div>
                </div>

                {/* Recent Commissions Table */}
                <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-gray-700 flex justify-between items-center">
                        <h3 className="text-xl font-bold text-white">Recent Earnings</h3>
                        <span className="text-xs text-gray-500">Last 10 transactions</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-900/50 text-gray-400 text-xs uppercase font-bold">
                                <tr>
                                    <th className="p-4">Doctor Name</th>
                                    <th className="p-4">Commission</th>
                                    <th className="p-4">Date</th>
                                    <th className="p-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {recent_commissions.map((comm, idx) => (
                                    <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                                        <td className="p-4 font-medium text-gray-200">{comm.doctor_name}</td>
                                        <td className="p-4 text-green-400 font-bold">+ ৳{comm.amount}</td>
                                        <td className="p-4 text-gray-400 text-sm">{new Date(comm.created_at).toLocaleDateString()}</td>
                                        <td className="p-4">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                                                comm.status === 'approved' ? 'bg-green-900/30 text-green-400 border-green-800' : 
                                                comm.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400 border-yellow-800' : 
                                                'bg-red-900/30 text-red-400 border-red-800'
                                            }`}>
                                                {comm.status.toUpperCase()}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {recent_commissions.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="p-10 text-center text-gray-500">
                                            No earnings yet. Start sharing your link to earn!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Withdraw Modal Component */}
                <WithdrawModal 
                    isOpen={isWithdrawOpen} 
                    onClose={() => setIsWithdrawOpen(false)} 
                    balance={profile.wallet_balance}
                    onSuccess={loadDashboardData} 
                />

            </div>
        </div>
    );
};

export default AffiliateDashboard;